import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { createEntity, getEntity, reset, updateEntity } from 'app/entities/place/place.reducer';
import {
  getStorageOptions,
  IdStorageTypes,
  setStorageOptions,
  ValuesStorageTypes,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/storageOptions';
import { IPosition } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';
import { AutoCompleteSearch } from 'app/shared/components/autocomplete-search/AutoCompleteSearch';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';
import { mechanicalWorkshopIcon } from '../../shared/util/iconStylesAndClusters';
import { PlaceForms } from './PlaceForms';

const PlaceMap = ({ props }) => {
  const dispatch = useAppDispatch();
  const [zoom] = useState<number>(4);
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [type, setType] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [placeLatitude, setPlaceLatitude] = useState(-23.486707);
  const [placeLongitude, setPlaceLongitude] = useState(-46.515203);
  const [isAutoComplete, setIsAutoComplete] = useState<boolean>(false);
  const [center] = useState<IPosition>({ lat: placeLatitude, lng: placeLongitude });
  const [mapType, setMapType] = useState<ValuesStorageTypes>(getStorageOptions(IdStorageTypes.MAP_TYPE) ?? 'MAP');
  const [isActivated, setIsActivated] = useState<boolean>(true);
  const loading = useAppSelector(state => state.place.loading);
  const updateSuccess = useAppSelector(state => state.place.updateSuccess);
  const mapRef = useRef(null);
  const placeList = useAppSelector(state => state.place.entities);
  const placeEntity = useAppSelector(state => state.place.entity);

  const handleClose = () => {
    dispatch(reset());
    props.history.goBack();
  };

  function onClickMapType() {
    document.getElementsByClassName('gm-style-mtc')[0].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  }

  const saveEntity = values => {
    const entity = {
      ...placeEntity,
      ...values,
      latitude: placeLatitude,
      longitude: placeLongitude,
      approved: true,
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const setFitBounds = () => {
    const bounds = new window.google.maps.LatLngBounds();
    const listBounds = [];

    if (placeLatitude && placeLongitude && isAutoComplete) {
      listBounds.push(
        { lat: placeLatitude, lng: placeLongitude },
        { lat: placeLatitude + 0.05, lng: placeLongitude + 0.05 },
        { lat: placeLatitude - 0.05, lng: placeLongitude - 0.05 }
      );
    } else if (placeEntity && placeEntity.latitude === placeLatitude && placeEntity.longitude === placeLongitude) {
      listBounds.push(
        { lat: placeEntity.latitude, lng: placeEntity.longitude },
        { lat: placeEntity.latitude + 0.05, lng: placeEntity.longitude + 0.05 },
        { lat: placeEntity.latitude - 0.05, lng: placeEntity.longitude - 0.05 }
      );
    }

    listBounds?.length &&
      listBounds.map(item => {
        if (item.lat && item.lng) {
          bounds.extend({ lat: item.lat, lng: item.lng });
          mapRef.current.fitBounds(bounds);
        }
      });
  };

  useEffect(() => {
    (!isNew || isAutoComplete) && setFitBounds();
  }, [placeEntity, isNew, placeLatitude, placeLongitude, isAutoComplete]);

  const renderLoading = useCallback(
    () =>
      loading && (
        <div className="spinner-map">
          <Spinner color='info'/>
        </div>
      ),
    [loading]
  );

  const renderButtonRoutes = useCallback(() => {
    const getDivButtonSearch = document.querySelector('.div-button-search');

    const getDivAutoComplete = document.querySelector('.div-auto-complete');

    const divMap = document.querySelector('.gm-style');

    if (!getDivButtonSearch) {
      const divButtonSearch = document.createElement('div');

      divButtonSearch.classList.add('div-button-search');

      divMap?.append(divButtonSearch);
    }

    if (!getDivAutoComplete) {
      const divAutoComplete = document.createElement('div');

      divAutoComplete.classList.add('div-auto-complete');

      divMap?.append(divAutoComplete);
    }

    if (!loading && getDivButtonSearch) {
      ReactDOM.render(
        <PlaceForms name={name} type={type} saveEntity={saveEntity} placeList={placeList} setName={setName} setType={setType} />,
        getDivButtonSearch
      );
    }

    if (!loading && getDivAutoComplete) {
      ReactDOM.render(
        <AutoCompleteSearch setLatitude={setPlaceLatitude} setLongitude={setPlaceLongitude} setIsAutoComplete={setIsAutoComplete} />,
        getDivAutoComplete
      );
    }
  }, [name, type, placeEntity, placeList, loading, placeLatitude, placeLongitude]);

  const renderMarker = useCallback(() => {
    return (
      <Marker
        draggable
        onDragEnd={e => [setPlaceLatitude(e.latLng.lat()), setPlaceLongitude(e.latLng.lng())]}
        icon={{
          url: mechanicalWorkshopIcon.url,
          scaledSize: new google.maps.Size(mechanicalWorkshopIcon.scaledSize.width, mechanicalWorkshopIcon.scaledSize.height),
        }}
        position={{ lat: placeLatitude, lng: placeLongitude }}
        label={{ text: !name ? null : name, className: 'label-icon' }}
      />
    );
  }, [placeLatitude, placeLongitude, name]);

  const setEditValues = useCallback(() => {
    setPlaceLongitude(placeEntity.longitude ?? placeLongitude);
    setPlaceLatitude(placeEntity.latitude ?? placeLatitude);
    setType(placeEntity.type ?? '');
    setName(placeEntity.name ?? '');
    setIsActivated(placeEntity.approved ?? isActivated);
  }, [placeEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    setEditValues();
  }, [setEditValues]);

  useEffect(() => {
    setTimeout(() => {
      isNew && renderButtonRoutes();
    }, 200);
  }, [renderButtonRoutes, isNew]);

  return (
    <GoogleMap
      ref={mapRef}
      zoom={zoom}
      center={center}
      defaultOptions={{ styles: mapStyle }}
      onTilesLoaded={renderButtonRoutes}
      onMapTypeIdChanged={onClickMapType}
      mapTypeId={google.maps.MapTypeId[mapType]}
      onClick={event => {
        setIsAutoComplete(false);
        setPlaceLatitude(Number(event.latLng.lat()));
        setPlaceLongitude(Number(event.latLng.lng()));
      }}
    >
      {renderLoading()}
      {!loading && renderButtonRoutes()}
      {!loading && renderMarker()}
    </GoogleMap>
  );
};

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

export default compose(
  withProps({
    googleMapURL: G_API_URL,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `72vh` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(PlaceMap);
